import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const { user, token } = useAuth();
  const [darkMode, setDarkMode] = useState(false);

  // Apply dark class to document element
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Load user preference on login
  useEffect(() => {
    if (user && user.dark_mode !== undefined) {
      setDarkMode(!!user.dark_mode);
    }
  }, [user]);

  // Toggle dark mode and persist to API
  const toggleDarkMode = useCallback(async () => {
    const newValue = !darkMode;
    setDarkMode(newValue);

    if (token) {
      try {
        await fetch('/api/settings', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ dark_mode: newValue }),
        });
      } catch (err) {
        console.error('Failed to save dark mode preference:', err);
      }
    }
  }, [darkMode, token]);

  // Set dark mode directly (used by settings page to sync)
  const setDarkModeValue = useCallback((value) => {
    setDarkMode(value);
  }, []);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode, setDarkModeValue }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
